import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';
import InlineLeadNotes from '../components/InlineLeadNotes';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Beirut', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const followupDate = lead => String(lead.academy_followup_date || lead.next_follow_up?.scheduled_at || '').slice(0, 10);

export default function LeadDirectory() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [showCreate, setShowCreate] = useState(false);
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [page, setPage] = useState(1);

  const defaultForm = {
    lead_name: '', lead_type: 'company', company_name: '', phone: '', email: '', website: '',
    country: '', address: '', priority: 'medium', status: 'new', lead_source_id: '', assigned_owner_id: '',
    notes: '', tags: '',
    contact_name: '', contact_email: '', contact_phone: '', contact_position: '',
  };
  const [form, setForm] = useState({ ...defaultForm });

  const fetchLeads = useCallback(() => {
    if (!companyId || companyId === 'holding') return;
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 25, sort: sortField, direction: sortDir });
    if (search) params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);
    if (priorityFilter) params.append('priority', priorityFilter);
    if (sourceFilter) params.append('lead_source_id', sourceFilter);
    if (ownerFilter) params.append('assigned_owner_id', ownerFilter);
    if (typeFilter) params.append('lead_type', typeFilter);

    fetch(`${API}/companies/${companyId}/leads?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setLeads(Array.isArray(data.data) ? data.data : []);
        setPagination({ current_page: data.current_page, last_page: data.last_page, total: data.total });
      })
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  }, [companyId, page, search, statusFilter, priorityFilter, sourceFilter, ownerFilter, typeFilter, sortField, sortDir]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    if (!companyId || companyId === 'holding') return;
    fetch(`${API}/companies/${companyId}/leads/sources?status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setSources(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/owners`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId]);

  const saveLeadNotes = async (lead, notes) => {
    const response = await fetch(`${API}/companies/${companyId}/leads/${lead.id}`, {
      method: 'PUT', headers: getAuthHeaders({'Content-Type':'application/json'}), body: JSON.stringify({notes}),
    });
    const result = await response.json().catch(()=>({}));
    if (!response.ok) throw new Error(result.message || 'Notes could not be saved.');
    setLeads(current=>current.map(item=>item.id===lead.id?{...item,notes:result.notes??''}:item));
    return result.notes??'';
  };

  const createLead = () => {
    const payload = { ...form };
    // Parse tags
    if (payload.tags) {
      payload.tags = payload.tags.split(',').map(t => t.trim()).filter(Boolean);
    } else {
      payload.tags = [];
    }
    // Extract primary contact data
    const contactData = {};
    if (payload.contact_name) contactData.name = payload.contact_name;
    if (payload.contact_email) contactData.email = payload.contact_email;
    if (payload.contact_phone) contactData.phone = payload.contact_phone;
    if (payload.contact_position) contactData.position = payload.contact_position;
    delete payload.contact_name;
    delete payload.contact_email;
    delete payload.contact_phone;
    delete payload.contact_position;

    if (contactData.name) {
      payload.primary_contact = contactData;
    }

    fetch(`${API}/companies/${companyId}/leads`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowCreate(false); setForm({ ...defaultForm }); fetchLeads(); })
      .catch(() => window.alert('Failed to create lead'));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === leads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(leads.map(l => l.id));
    }
  };

  const executeBulkAction = () => {
    if (!bulkAction || selectedIds.length === 0) return;
    if (!window.confirm(`Apply "${bulkAction}" to ${selectedIds.length} lead(s)?`)) return;

    const promises = selectedIds.map(id => {
      let url = `${API}/companies/${companyId}/leads/${id}`;
      let method = 'PUT';
      let body = {};

      if (bulkAction === 'archive') {
        url += '/archive';
        method = 'POST';
      } else if (bulkAction === 'delete') {
        method = 'DELETE';
      } else if (bulkAction.startsWith('status:')) {
        body = { status: bulkAction.split(':')[1] };
      } else if (bulkAction.startsWith('priority:')) {
        body = { priority: bulkAction.split(':')[1] };
      }

      return fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: method !== 'DELETE' && Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });
    });

    Promise.all(promises).then(() => {
      setSelectedIds([]);
      setBulkAction('');
      fetchLeads();
    }).catch(() => window.alert('Some bulk operations failed'));
  };

  if (!companyId || companyId === 'holding') return <div style={{ padding: 32 }}><h2>Select a company</h2></div>;

  const priorityColors = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };
  const statusColors = { new: '#3b82f6', contacted: '#8b5cf6', qualified: '#22c55e', unqualified: '#6b7280', active: '#10b981', inactive: '#9ca3af', archived: '#d1d5db' };

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const selectStyle = { ...inputStyle };
  const labelStyle = { fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4, color: '#374151' };

  const sortIcon = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const thSortable = (label, field) => (
    <th onClick={() => handleSort(field)}
      style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
      {label}<span style={{ color: sortField === field ? '#6366f1' : '#d1d5db', fontSize: 12 }}>{sortIcon(field)}</span>
    </th>
  );

  return (
    <div style={{ padding: 32, maxWidth: 1500 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 2 }}>Lead Directory</h2>
          {pagination.total !== undefined && <span style={{ fontSize: 13, color: '#6b7280' }}>{pagination.total} leads total</span>}
        </div>
        <button onClick={() => setShowCreate(true)} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>+ New Lead</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Search leads..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 240 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ ...selectStyle, maxWidth: 150 }}>
          <option value="">All Statuses</option>
          {['new','contacted','qualified','unqualified','active','inactive','archived'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); }} style={{ ...selectStyle, maxWidth: 130 }}>
          <option value="">All Priorities</option>
          {['high','medium','low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} style={{ ...selectStyle, maxWidth: 140 }}>
          <option value="">All Types</option>
          <option value="company">Company</option>
          <option value="individual">Individual</option>
        </select>
        <select value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setPage(1); }} style={{ ...selectStyle, maxWidth: 160 }}>
          <option value="">All Sources</option>
          {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={ownerFilter} onChange={e => { setOwnerFilter(e.target.value); setPage(1); }} style={{ ...selectStyle, maxWidth: 160 }}>
          <option value="">All Owners</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', background: '#eef2ff', padding: '10px 16px', borderRadius: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#4f46e5' }}>{selectedIds.length} selected</span>
          <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} style={{ ...selectStyle, maxWidth: 200 }}>
            <option value="">Bulk Action...</option>
            <optgroup label="Change Status">
              {['new','contacted','qualified','unqualified','active','inactive'].map(s => (
                <option key={s} value={`status:${s}`}>Status → {s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </optgroup>
            <optgroup label="Change Priority">
              {['high','medium','low'].map(p => (
                <option key={p} value={`priority:${p}`}>Priority → {p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </optgroup>
            <option value="archive">Archive</option>
            <option value="delete">Delete</option>
          </select>
          <button onClick={executeBulkAction} disabled={!bulkAction}
            style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: bulkAction ? '#4f46e5' : '#c7d2fe', color: '#fff', fontWeight: 600, cursor: bulkAction ? 'pointer' : 'default', fontSize: 13 }}>
            Apply
          </button>
          <button onClick={() => setSelectedIds([])} style={{ padding: '6px 12px', border: '1px solid #c7d2fe', borderRadius: 6, background: '#fff', color: '#4f46e5', cursor: 'pointer', fontSize: 13 }}>Clear</button>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '12px 10px', width: 40 }}>
                <input type="checkbox" checked={leads.length > 0 && selectedIds.length === leads.length} onChange={toggleSelectAll} />
              </th>
              {thSortable('Code', 'lead_code')}
              {thSortable('Lead Name', 'lead_name')}
              {thSortable('Status', 'status')}
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Notes</th>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Original price</th>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Type</th>
              {thSortable('Company', 'company_name')}
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Email</th>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Phone</th>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Country</th>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Source</th>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Owner</th>
              {thSortable('Priority', 'priority')}
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Tags</th>
              {thSortable('Last Activity', 'last_activity_at')}
              {thSortable('Created', 'created_at')}
              <th style={{ padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>Follow-up date</th>
            </tr>
          </thead>
          <tbody>
            {initialLoading ? (
              <tr><td colSpan={18} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={18} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No leads found</td></tr>
            ) : leads.map(lead => (
              <tr key={lead.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                onMouseOver={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseOut={e => e.currentTarget.style.background = '#fff'}>
                <td style={{ padding: '10px 10px' }} onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedIds.includes(lead.id)} onChange={() => toggleSelect(lead.id)} />
                </td>
                <td style={{ padding: '10px 14px', color: '#6366f1', fontWeight: 500, cursor: 'pointer' }} onClick={() => navigate(`/company/leads/${lead.id}`)}>{lead.lead_code}</td>
                <td style={{ padding: '10px 14px', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate(`/company/leads/${lead.id}`)}>{lead.lead_name}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', background: statusColors[lead.status] || '#6b7280' }}>
                    {lead.status}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', minWidth: 260, maxWidth: 360, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13, lineHeight: 1.5, verticalAlign: 'top' }}><InlineLeadNotes value={lead.notes} name={lead.lead_name} onSave={notes=>saveLeadNotes(lead,notes)}/></td>
                <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>{lead.original_price == null ? '—' : Number(lead.original_price).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                <td style={{ padding: '10px 14px', fontSize: 12 }}>
                  <span style={{ padding: '2px 8px', borderRadius: 4, background: lead.lead_type === 'company' ? '#dbeafe' : '#fce7f3', color: lead.lead_type === 'company' ? '#1d4ed8' : '#be185d', fontSize: 11, fontWeight: 600 }}>
                    {lead.lead_type === 'company' ? '🏢' : '👤'} {lead.lead_type}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>{lead.company_name || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13 }}>{lead.email || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13 }}>{lead.phone || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13 }}>{lead.country || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13 }}>{lead.source?.name || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13 }}>{lead.owner?.name || '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', background: priorityColors[lead.priority] || '#6b7280' }}>
                    {lead.priority}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {Array.isArray(lead.tags) && lead.tags.length > 0 ? (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {lead.tags.slice(0, 3).map((t, i) => (
                        <span key={i} style={{ padding: '1px 6px', borderRadius: 4, background: '#f3f4f6', color: '#4b5563', fontSize: 11 }}>{t}</span>
                      ))}
                      {lead.tags.length > 3 && <span style={{ fontSize: 11, color: '#9ca3af' }}>+{lead.tags.length - 3}</span>}
                    </div>
                  ) : '—'}
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {lead.last_activity_at ? new Date(lead.last_activity_at).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {followupDate(lead) ? <button onClick={() => navigate(`/company/leads/${lead.id}`)} title={followupDate(lead) < today() ? 'Overdue follow-up' : 'View follow-ups'} style={{ padding: '7px 10px', border: `1px solid ${followupDate(lead) < today() ? '#f4b4ad' : '#cfdae8'}`, borderRadius: 7, background: followupDate(lead) < today() ? '#fff0ee' : '#fff', color: followupDate(lead) < today() ? '#b42318' : '#254265', cursor: 'pointer', whiteSpace: 'nowrap' }}>{followupDate(lead)}</button> : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}>
            ← Prev
          </button>
          {/* Show page numbers smartly */}
          {(() => {
            const pages = [];
            const last = pagination.last_page;
            let start = Math.max(1, page - 2);
            let end = Math.min(last, page + 2);
            if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
            for (let i = start; i <= end; i++) pages.push(i);
            if (end < last) { if (end < last - 1) pages.push('...'); pages.push(last); }
            return pages.map((p, i) => (
              p === '...' ? <span key={`e${i}`} style={{ padding: '6px 4px', color: '#9ca3af' }}>...</span> : (
                <button key={p} onClick={() => setPage(p)}
                  style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: page === p ? '#6366f1' : '#fff', color: page === p ? '#fff' : '#374151', cursor: 'pointer', fontWeight: page === p ? 600 : 400 }}>
                  {p}
                </button>
              )
            ));
          })()}
          <button onClick={() => setPage(Math.min(pagination.last_page, page + 1))} disabled={page === pagination.last_page}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: page === pagination.last_page ? 'default' : 'pointer', opacity: page === pagination.last_page ? 0.5 : 1 }}>
            Next →
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 20, fontSize: 18, fontWeight: 700 }}>Create New Lead</h3>

            {/* Lead Info */}
            <div style={{ fontSize: 14, fontWeight: 700, color: '#6366f1', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #e5e7eb' }}>Lead Information</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Lead Name *</label>
                <input style={inputStyle} value={form.lead_name} onChange={e => setForm({ ...form, lead_name: e.target.value })} placeholder="Enter lead name" />
              </div>
              <div>
                <label style={labelStyle}>Type</label>
                <select style={selectStyle} value={form.lead_type} onChange={e => setForm({ ...form, lead_type: e.target.value })}>
                  <option value="company">Company</option>
                  <option value="individual">Individual</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Company Name</label>
                <input style={inputStyle} value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input style={inputStyle} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Website</label>
                <input style={inputStyle} value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://" />
              </div>
              <div>
                <label style={labelStyle}>Country</label>
                <input style={inputStyle} value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Address</label>
                <input style={inputStyle} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Priority</label>
                <select style={selectStyle} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Source</label>
                <select style={selectStyle} value={form.lead_source_id} onChange={e => setForm({ ...form, lead_source_id: e.target.value })}>
                  <option value="">Select source</option>
                  {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Assigned Owner</label>
                <select style={selectStyle} value={form.assigned_owner_id} onChange={e => setForm({ ...form, assigned_owner_id: e.target.value })}>
                  <option value="">Select owner</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Tags <span style={{ fontWeight: 400, color: '#9ca3af' }}>(comma-separated)</span></label>
                <input style={inputStyle} value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="e.g. VIP, Enterprise, Tech" />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes about this lead..." />
              </div>
            </div>

            {/* Primary Contact */}
            <div style={{ fontSize: 14, fontWeight: 700, color: '#6366f1', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #e5e7eb' }}>Primary Contact (Optional)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={labelStyle}>Contact Name</label>
                <input style={inputStyle} value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Position</label>
                <input style={inputStyle} value={form.contact_position} onChange={e => setForm({ ...form, contact_position: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Contact Email</label>
                <input style={inputStyle} type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Contact Phone</label>
                <input style={inputStyle} value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowCreate(false); setForm({ ...defaultForm }); }} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={createLead} disabled={!form.lead_name}
                style={{ padding: '8px 20px', border: 'none', borderRadius: 6, background: '#6366f1', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: form.lead_name ? 1 : 0.5 }}>
                Create Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
